import type { ProfileSort } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { FilterGroup } from '@/types/filters';

import type { ProfilesPage, TrackedLpsGateway } from '../infrastructure/tracked-lps.gateway';
import {
  favoriteRemoved,
  filtersChanged,
  pageChanged,
  pageSizeChanged,
  sortChanged,
  trackedLpsRequested,
} from './tracked-lps.actions';
import { selectFilters, selectPage, selectPageSize, selectSort } from './tracked-lps.selectors';
import { trackedLpsActions } from './tracked-lps.slice';

const LOAD_ERROR = 'Failed to load tracked LPs.';

/** Best-effort human message for the failure banner (no domain error union). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER: fetch the current page. Reads `page` / `pageSize` / `filters` / `sort` back from
 * the store (the single source of truth for the fetch) and calls the gateway.
 * `takeLatest` cancels an in-flight fetch when a newer trigger arrives; the
 * abort-latest behaviour the old `useInfiniteApi` had.
 */
function* fetchCurrentPage() {
  const gateway: TrackedLpsGateway = yield getContext('trackedLpsGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);
  const filters: FilterGroup | null = yield select(selectFilters);
  const sort: ProfileSort | null = yield select(selectSort);

  yield put(trackedLpsActions.loadStarted());
  try {
    const result = (yield call(gateway.fetchProfiles, {
      filters,
      page,
      pageSize,
      sort,
    })) as ProfilesPage;
    yield put(trackedLpsActions.loadSucceeded(result));
  } catch (e) {
    yield put(trackedLpsActions.loadFailed(toMessage(e, LOAD_ERROR)));
  }
}

/** WORKER: apply new server filters (resets to page 0), then fetch. */
function* filtersChangedWorker(action: ReturnType<typeof filtersChanged>) {
  yield put(trackedLpsActions.filtersSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: apply a new sort (resets to page 0), then fetch. */
function* sortChangedWorker(action: ReturnType<typeof sortChanged>) {
  yield put(trackedLpsActions.sortSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: move to a page, then fetch it. */
function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(trackedLpsActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: change the page size (resets to page 0), then fetch. */
function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(trackedLpsActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/**
 * WORKER: refetch the current page. An un-favorite first drops its row
 * (optimistic); the shared FavoriteButton only calls back once its POST has
 * landed, so the refetch needs no grace delay. A plain request (first load,
 * add-tags, Undo re-tracking a profile) refetches as is. One `takeLatest` for
 * both (#694): an Undo clicked right after an un-favorite cancels that refetch
 * instead of racing it, so a stale empty page never lands after the restored one.
 */
function* refetchWorker(
  action: ReturnType<typeof favoriteRemoved> | ReturnType<typeof trackedLpsRequested>,
) {
  if (favoriteRemoved.match(action)) {
    yield put(trackedLpsActions.rowRemoved(action.payload));
  }
  yield* fetchCurrentPage();
}

/** WATCHERS. */
export function* trackedLpsSaga() {
  yield all([
    takeLatest([trackedLpsRequested.type, favoriteRemoved.type], refetchWorker),
    takeLatest(filtersChanged.type, filtersChangedWorker),
    takeLatest(sortChanged.type, sortChangedWorker),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
  ]);
}
